import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';

/**
 * ConsultationCartContext
 *
 * A simple wishlist / consultation cart. It is NOT a checkout cart.
 * Items are sent to Morea by WhatsApp (Quito or Guayaquil) for availability
 * confirmation. No payment, no online rental.
 *
 * Item shape:
 *   { id, name, type: 'vestido' | 'bolso' | 'tacón' | 'arete' }
 */

const STORAGE_KEY = 'morea_ec_consultation_cart_v1';

const CartContext = createContext({
  items: [],
  addItem: () => {},
  removeItem: () => {},
  clear: () => {},
  isOpen: false,
  open: () => {},
  close: () => {},
  getSelectedDates: () => '',
});

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      /* noop */
    }
  }, [items]);

  const addItem = useCallback((item) => {
    setItems((prev) => {
      // Avoid duplicates by name (case-insensitive)
      const exists = prev.some(
        (i) => i.name.toLowerCase() === item.name.toLowerCase()
      );
      if (exists) return prev;
      return [...prev, { ...item, id: item.id || `${Date.now()}-${Math.random()}` }];
    });
  }, []);

  const removeItem = useCallback((id) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  /**
   * Read selected dates from Booqable's datepicker input(s).
   * Booqable renders inputs inside .booqable-datepicker; we read their values.
   */
  const getSelectedDates = useCallback(() => {
    const inputs = document.querySelectorAll('.booqable-datepicker input');
    const values = Array.from(inputs)
      .map((el) => (el.value || '').trim())
      .filter(Boolean);
    if (values.length === 0) return '';
    if (values.length === 1) return values[0];
    return `${values[0]} – ${values[values.length - 1]}`;
  }, []);

  const value = useMemo(
    () => ({
      items,
      addItem,
      removeItem,
      clear,
      isOpen,
      open: () => setIsOpen(true),
      close: () => setIsOpen(false),
      getSelectedDates,
    }),
    [items, addItem, removeItem, clear, isOpen, getSelectedDates]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  return useContext(CartContext);
}

/**
 * Classify a product by its name into a cart item type.
 * Falls back to 'vestido' if no accessory keyword is found AND a defaultType
 * is not provided.
 */
export function classifyProduct(name, defaultType = 'vestido') {
  const n = (name || '').toLowerCase();
  if (/(arete|earring)/.test(n)) return 'arete';
  if (/(tac[oó]n|zapato|shoe|heel)/.test(n)) return 'tacón';
  if (/(bolso|cartera|clutch|bag|handbag)/.test(n)) return 'bolso';
  return defaultType;
}
